import { Input, Text } from '@/components/DesignSystem';
import { validatePassword } from '@shared/utilities';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

const getChecks = (password: string) => ({
  isAtLeast8Characters: password.length >= 8,
  isAtLeast16Characters: password.length >= 16,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecial: /[^A-Za-z0-9]/.test(password),
});

const Requirement = ({ met, label }: { met: boolean; label: string }) => (
  <Stack alignItems="center" direction="row" spacing={1}>
    <Text category="body-medium" color={met ? 'success' : undefined} disabled={!met}>
      {met ? '✓' : '○'}
    </Text>
    <Text
      category="body-medium"
      color={met ? 'success' : undefined}
      sx={met ? undefined : { color: 'text.secondary' }}
    >
      {label}
    </Text>
  </Stack>
);

interface PasswordInputFieldsProps {
  newPassword: string;
  onNewPasswordChange: (value: string) => void;
  confirmPassword: string;
  onConfirmPasswordChange: (value: string) => void;
  disabled?: boolean;
}

export const PasswordInputFields = ({
  confirmPassword,
  disabled,
  newPassword,
  onConfirmPasswordChange,
  onNewPasswordChange,
}: PasswordInputFieldsProps) => {
  const { t } = useTranslation();
  const checks = getChecks(newPassword);
  const isValid = validatePassword(newPassword);

  return (
    <>
      <Input
        disabled={disabled}
        fullWidth
        label={t($ => $.auth.passwordReset.newPassword)}
        margin="normal"
        onChange={(e) => onNewPasswordChange(e.target.value)}
        required
        testID="password-input"
        type="password"
        value={newPassword}
        variant="outlined"
      />
      {newPassword.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1, mb: 1 }}>
          {isValid ? (
            <Text category="body-medium" color="success">
              {t($ => $.auth.passwordReset.passwordMeetsRequirements)}
            </Text>
          ) : (
            <>
              <Requirement
                label={t($ => $.auth.passwordReset.requirement16Characters)}
                met={checks.isAtLeast16Characters}
              />
              <Text category="body-medium" disabled>
                {t($ => $.auth.passwordReset.requirementOrAllOf)}
              </Text>
              <Requirement
                label={t($ => $.auth.passwordReset.requirement8Characters)}
                met={checks.isAtLeast8Characters}
              />
              <Requirement
                label={t($ => $.auth.passwordReset.requirementUppercase)}
                met={checks.hasUppercase}
              />
              <Requirement
                label={t($ => $.auth.passwordReset.requirementLowercase)}
                met={checks.hasLowercase}
              />
              <Requirement
                label={t($ => $.auth.passwordReset.requirementNumber)}
                met={checks.hasNumber}
              />
              <Requirement
                label={t($ => $.auth.passwordReset.requirementSpecial)}
                met={checks.hasSpecial}
              />
            </>
          )}
        </Stack>
      )}
      <Input
        disabled={disabled}
        fullWidth
        label={t($ => $.auth.passwordReset.confirmPassword)}
        margin="normal"
        onChange={(e) => onConfirmPasswordChange(e.target.value)}
        required
        testID="confirm-password-input"
        type="password"
        value={confirmPassword}
        variant="outlined"
      />
    </>
  );
};
